import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Radar, Users, Search,
  Building2, Zap, Save, ShieldCheck, CheckCircle, RefreshCw, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const navItems = [
  { id: "dashboard", label: "Dashboard", path: "/corporate-dashboard", icon: LayoutDashboard },
  { id: "match-engine", label: "Match Engine", path: "/corporate-dashboard", icon: Radar },
  { id: "partners", label: "Partners", path: "/corporate-dashboard", icon: Users },
];

const SECTOR_OPTIONS = [
  "Healthcare", "Education", "Environment", "Livelihoods",
  "Women Empowerment", "Rural Development", "Sanitation", "Technology"
];

function NgoMatchCard({ ngo, onInitiateGrant, grantLoading }) {
  return (
    <div
      className="p-4 rounded-lg border border-slate-200 bg-white hover:shadow-sm transition-shadow"
      data-testid={`ngo-match-card-${ngo.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 bg-emerald-900/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-emerald-800" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-slate-900 truncate">{ngo.org_name}</h4>
            <Badge className="bg-emerald-600 text-white border-0 text-[10px] px-1.5 py-0 h-5 gap-1" data-testid={`ngo-verified-badge-${ngo.id}`}>
              <CheckCircle className="w-3 h-3" />
              Verified
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ngo.matching_sectors.map((s) => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium">
                {s}
              </span>
            ))}
            {ngo.focus_sectors.filter((s) => !ngo.matching_sectors.includes(s)).map((s) => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200">
                {s}
              </span>
            ))}
          </div>
          {ngo.registration_number && (
            <p className="text-[11px] text-slate-400 mt-1.5">Reg: {ngo.registration_number}</p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <span className="font-sans text-xl font-bold text-emerald-900" data-testid={`ngo-xscore-${ngo.id}`}>
            {ngo.x_score}
          </span>
          <p className="text-[10px] text-slate-400 mt-0.5">X-Score</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <Button
          className="w-full bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm h-9 text-sm"
          onClick={() => onInitiateGrant(ngo)}
          disabled={grantLoading}
          data-testid={`initiate-grant-btn-${ngo.id}`}
        >
          Initiate CSR Grant
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

export default function CorporateDashboard() {
  const { api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [matchedNgos, setMatchedNgos] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantModal, setGrantModal] = useState({ open: false, ngoName: "" });
  const [form, setForm] = useState({
    company_name: "",
    csr_budget: 0,
    focus_sectors: [],
  });

  const loadMatches = useCallback(async () => {
    setMatchLoading(true);
    try {
      const { data } = await api.get("/corporate/recommended-ngos");
      setMatchedNgos(data);
    } catch (e) {
      console.error("Failed to load matches", e);
    } finally {
      setMatchLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const init = async () => {
      try {
        const { data } = await api.get("/corporate/profile");
        setProfile(data);
        setForm({
          company_name: data.company_name || "",
          csr_budget: data.csr_budget || 0,
          focus_sectors: data.focus_sectors || [],
        });
      } catch (e) {
        console.error("Failed to load profile", e);
      } finally {
        setLoading(false);
      }
    };
    init().then(() => loadMatches());
  }, [api, loadMatches]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/corporate/profile", form);
      setProfile(data);
      toast.success("Profile updated successfully");
      await loadMatches();
    } catch (e) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleInitiateGrant = async (ngo) => {
    setGrantLoading(true);
    try {
      await api.post("/corporate/initiate-grant", { ngo_id: ngo.id });
      setGrantModal({ open: true, ngoName: ngo.org_name });
    } catch (e) {
      toast.error("Failed to initiate grant. Please try again.");
    } finally {
      setGrantLoading(false);
    }
  };

  const toggleSector = (sector) => {
    setForm(prev => ({
      ...prev,
      focus_sectors: prev.focus_sectors.includes(sector)
        ? prev.focus_sectors.filter(s => s !== sector)
        : [...prev.focus_sectors, sector]
    }));
  };

  const filteredNgos = searchQuery
    ? matchedNgos.filter(
        (ngo) =>
          ngo.org_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          ngo.focus_sectors.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : matchedNgos;

  if (loading) {
    return (
      <DashboardLayout navItems={navItems}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems}>
      <div className="space-y-8" data-testid="corporate-dashboard">
        {/* Welcome Header */}
        <div>
          <h2 className="font-sans text-2xl font-semibold text-slate-900 tracking-tight">
            Welcome back
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Set up your CSR profile and discover verified NGO partners.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Company Profile Card */}
          <Card className="lg:col-span-1 bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="corporate-profile-card">
            <CardHeader>
              <CardTitle className="font-sans text-lg text-slate-900">Company Profile</CardTitle>
              <CardDescription className="text-slate-500">Your CSR team details</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">Company Name</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Enter company name"
                  className="mt-1.5 focus-visible:ring-emerald-600"
                  data-testid="corporate-company-name-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">CSR Budget (INR)</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={form.csr_budget === 0 ? "" : String(form.csr_budget)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, "");
                    setForm({ ...form, csr_budget: raw === "" ? 0 : parseFloat(raw) });
                  }}
                  placeholder="e.g., 5000000"
                  className="mt-1.5 focus-visible:ring-emerald-600"
                  data-testid="corporate-csr-budget-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Focus Sectors</Label>
                <div className="flex flex-wrap gap-2">
                  {SECTOR_OPTIONS.map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      onClick={() => toggleSector(sector)}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-all ${
                        form.focus_sectors.includes(sector)
                          ? "border-emerald-600 bg-emerald-50 text-emerald-800 font-medium"
                          : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}
                      data-testid={`sector-${sector.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                className="w-full bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm mt-4"
                onClick={handleSave}
                disabled={saving}
                data-testid="corporate-save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Profile"}
              </Button>
            </CardContent>
          </Card>

          {/* Match Engine Widget */}
          <Card className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="match-engine-widget">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-sans text-lg text-slate-900 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-emerald-600" />
                    Match Engine
                  </CardTitle>
                  <CardDescription className="text-slate-500 mt-1">
                    Verified NGOs matching your focus sectors (X-Score 50+)
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-500 hover:text-emerald-800"
                  onClick={loadMatches}
                  disabled={matchLoading}
                  data-testid="match-engine-refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${matchLoading ? "animate-spin" : ""}`} />
                </Button>
              </div>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              {/* Search Bar */}
              <div className="flex gap-3 mb-6">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    placeholder="Search matched NGOs by name or sector..."
                    className="pl-10 focus-visible:ring-emerald-600"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="match-engine-search"
                  />
                </div>
                {matchedNgos.length > 0 && (
                  <Badge variant="outline" className="self-center text-xs text-slate-600 border-slate-300 whitespace-nowrap">
                    {matchedNgos.length} match{matchedNgos.length !== 1 ? "es" : ""}
                  </Badge>
                )}
              </div>

              {/* Results */}
              <div data-testid="recommended-ngos-list">
                {matchLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : form.focus_sectors.length === 0 && profile?.focus_sectors?.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-10 text-center bg-slate-50/50 flex flex-col items-center justify-center" data-testid="match-engine-no-sectors">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                      <Radar className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">Select focus sectors to activate</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs">
                      Choose your CSR focus sectors in the profile card and save to find matching NGOs.
                    </p>
                  </div>
                ) : filteredNgos.length === 0 ? (
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-10 text-center bg-slate-50/50 flex flex-col items-center justify-center" data-testid="match-engine-no-matches">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                      <Building2 className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">
                      {searchQuery ? "No NGOs match your search" : "No verified NGOs found for your sectors"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1 max-w-sm">
                      {searchQuery
                        ? "Try a different search term."
                        : "Only NGOs with an X-Score of 50+ appear here. Check back as more NGOs complete verification."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredNgos.map((ngo) => (
                      <NgoMatchCard
                        key={ngo.id}
                        ngo={ngo}
                        onInitiateGrant={handleInitiateGrant}
                        grantLoading={grantLoading}
                      />
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Grant Success Modal */}
      <Dialog open={grantModal.open} onOpenChange={(open) => setGrantModal({ ...grantModal, open })}>
        <DialogContent className="sm:max-w-md border-slate-200" data-testid="grant-success-modal">
          <div className="flex flex-col items-center text-center pt-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-5">
              <CheckCircle className="w-8 h-8 text-emerald-700" />
            </div>
            <DialogHeader className="items-center">
              <DialogTitle className="font-sans text-xl text-slate-900" data-testid="grant-modal-title">
                Funding Intent Secured
              </DialogTitle>
              <DialogDescription className="text-slate-600 mt-3 text-sm leading-relaxed max-w-sm" data-testid="grant-modal-description">
                ImpactX Bridge is verifying compliance and preparing the secure disbursement ledger.
              </DialogDescription>
            </DialogHeader>
            {grantModal.ngoName && (
              <div className="mt-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
                <p className="text-xs text-emerald-800 font-medium" data-testid="grant-modal-ngo-name">
                  Partner: {grantModal.ngoName}
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="mt-4 sm:justify-center">
            <Button
              className="bg-ix-red-600 text-white hover:bg-ix-red-700 shadow-sm px-8"
              onClick={() => setGrantModal({ open: false, ngoName: "" })}
              data-testid="grant-modal-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
